import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  ArrowLeft,
  Bot,
  Check,
  Copy,
  Lightbulb,
  RotateCcw,
  Send,
  Sparkles,
  User,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useLocation, useNavigate } from "react-router-dom";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  limit,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

const functions = getFunctions();
const askAIMentorChat = httpsCallable(functions, "askAIMentorChat");
const saveNoteFn = httpsCallable(functions, "saveUserNote");

const STARTER_MESSAGE = {
  role: "assistant",
  content:
    "I am here to help you move forward without overthinking. Ask me about today's roadmap, a blocker, what to prioritize next, or how to understand a topic simply.",
  meta: {
    type: "greeting",
    timestamp: Date.now(),
  },
};

const STARTER_PROMPTS = [
  "Explain today's task in simple words",
  "What should I focus on right now?",
  "I feel stuck. Help me break today's work into smaller steps",
  "What is the fastest way to make progress today?",
];

const formatMentorText = (text = "") => {
  const paragraphs = text.split("\n\n");

  return paragraphs.map((p, i) => {
    if (p.trim().startsWith("-") || p.trim().startsWith("•")) {
      const items = p
        .split("\n")
        .map((l) => l.replace(/^[-•]\s*/, "").trim())
        .filter(Boolean);

      return (
        <ul key={i}>
          {items.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      );
    }

    return <p key={i}>{p}</p>;
  });
};

const Page = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background:
    radial-gradient(680px at 14% 8%, rgba(245, 158, 11, 0.12), transparent 60%),
    radial-gradient(720px at 84% 12%, rgba(56, 189, 248, 0.14), transparent 62%),
    linear-gradient(180deg, rgba(8, 13, 24, 0.98), rgba(4, 8, 18, 0.98));
`;

const Header = styled.div`
  padding: 18px 18px 16px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: rgba(2, 6, 23, 0.44);
  backdrop-filter: blur(12px);

  @media (max-width: 640px) {
    padding: 14px;
  }
`;

const BackBtn = styled.button`
  height: 42px;
  width: 42px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.5);
  color: #e2e8f0;
  display: grid;
  place-items: center;
  cursor: pointer;
  flex-shrink: 0;
`;

const HeaderBody = styled.div`
  min-width: 0;
  flex: 1;
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.div`
  color: #f8fafc;
  font-size: 18px;
  font-weight: 1000;
  line-height: 1.2;
`;

const Sub = styled.div`
  margin-top: 7px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.6;
  max-width: 620px;
`;

const Presence = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.48);
  border: 1px solid rgba(148, 163, 184, 0.12);
  color: #86efac;
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
`;

const OnlineDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.6);
  animation: pulse 1.6s infinite;

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.6);
    }
    70% {
      box-shadow: 0 0 0 6px rgba(34, 197, 94, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
    }
  }
`;

const UtilityBar = styled.div`
  padding: 12px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  background: rgba(2, 6, 23, 0.26);

  @media (max-width: 640px) {
    padding: 12px 14px;
  }
`;

const UtilityBtn = styled.button`
  min-height: 38px;
  padding: 9px 13px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.46);
  color: #e2e8f0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
`;

const UsageWrap = styled.div`
  padding: 14px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(2, 6, 23, 0.2);

  @media (max-width: 640px) {
    padding: 14px;
  }
`;

const UsageRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 14px;
  font-size: 12px;
  color: #cbd5e1;
  margin-bottom: 8px;
`;

const UsageBar = styled.div`
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
`;

const UsageFill = styled.div`
  height: 100%;
  width: ${({ percent }) => percent}%;
  background: linear-gradient(90deg, #22c55e, #38bdf8, #6366f1);
  transition: width 0.4s ease;
`;

const ChatWrapper = styled.div`
  min-height: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PromptsScroller = styled.div`
  padding: 14px 18px 0;
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 640px) {
    padding: 14px 14px 0;
  }
`;

const PromptChip = styled.button`
  flex: 0 0 auto;
  padding: 10px 14px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.44);
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
`;

const Messages = styled.div`
  flex: 1;
  min-height: 0;
  padding: 18px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  scroll-behavior: smooth;

  @media (max-width: 640px) {
    padding: 14px;
  }
`;

const ChatRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
  ${({ role }) => (role === "user" ? "flex-direction: row-reverse;" : "")}
`;

const Avatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 14px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ role }) =>
    role === "user"
      ? "linear-gradient(135deg, #f59e0b, #facc15)"
      : "linear-gradient(135deg, #38bdf8, #6366f1)"};
  color: #04111f;
`;

const BubbleWrap = styled.div`
  max-width: min(82%, 560px);

  @media (max-width: 640px) {
    max-width: 88%;
  }
`;

const BubbleMeta = styled.div`
  margin: ${({ role }) => (role === "user" ? "0 4px 6px 0" : "0 0 6px 4px")};
  color: #64748b;
  font-size: 11px;
  display: flex;
  justify-content: ${({ role }) =>
    role === "user" ? "flex-end" : "flex-start"};
`;

const UserBubble = styled(motion.div)`
  padding: 13px 16px;
  border-radius: 22px 22px 8px 22px;
  line-height: 1.65;
  font-size: 14px;
  background: linear-gradient(135deg, #f59e0b, #facc15);
  color: #04111f;
  box-shadow: 0 18px 40px rgba(245, 158, 11, 0.18);
`;

const MentorBubble = styled(motion.div)`
  padding: 16px 18px;
  border-radius: 22px 22px 22px 8px;
  line-height: 1.75;
  font-size: 14px;
  background: rgba(15, 23, 42, 0.76);
  color: #e5e7eb;
  border: 1px solid rgba(56, 189, 248, 0.16);
  backdrop-filter: blur(14px);
  box-shadow: 0 10px 34px rgba(0, 0, 0, 0.3);

  p {
    margin: 0 0 10px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul {
    margin: 8px 0 12px 18px;
    padding: 0;
  }

  li {
    margin-bottom: 6px;
  }

  strong {
    color: #7dd3fc;
    font-weight: 800;
  }
`;

const BubbleActions = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ActionBtn = styled.button`
  min-height: 34px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 800;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: ${({ tone }) =>
    tone === "success"
      ? "rgba(34, 197, 94, 0.14)"
      : tone === "primary"
      ? "rgba(56, 189, 248, 0.12)"
      : "rgba(255, 255, 255, 0.05)"};
  color: ${({ tone }) =>
    tone === "success"
      ? "#86efac"
      : tone === "primary"
      ? "#7dd3fc"
      : "#cbd5e1"};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
`;

const Typing = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  color: #94a3b8;
  font-size: 13px;
`;

const TypingDots = styled.div`
  display: inline-flex;
  gap: 4px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #64748b;
    animation: bounce 1.1s infinite ease-in-out;
  }

  span:nth-child(2) {
    animation-delay: 0.15s;
  }

  span:nth-child(3) {
    animation-delay: 0.3s;
  }

  @keyframes bounce {
    0%,
    80%,
    100% {
      transform: scale(0.7);
      opacity: 0.55;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const Composer = styled.div`
  padding: 10px 14px calc(12px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid rgba(148, 163, 184, 0.12);
  background: rgba(2, 6, 23, 0.42);
  backdrop-filter: blur(14px);
  flex-shrink: 0;

  @media (max-width: 640px) {
    padding: 10px 12px calc(12px + env(safe-area-inset-bottom, 0px));
  }
`;

const ComposerShell = styled.div`
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.62);
  overflow: hidden;
`;

const Input = styled.textarea`
  width: 100%;
  resize: none;
  border: none;
  background: transparent;
  color: #e5e7eb;
  outline: none;
  min-height: 56px;
  max-height: 110px;
  padding: 12px 14px 6px;
  font-size: 14px;
  line-height: 1.65;

  &::placeholder {
    color: #64748b;
  }

  @media (max-width: 640px) {
    min-height: 52px;
  }
`;

const ComposerFooter = styled.div`
  padding: 8px 10px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ComposerHint = styled.div`
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.5;
`;

const ComposerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;

  @media (max-width: 640px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const CharacterCount = styled.div`
  color: ${({ warn }) => (warn ? "#fca5a5" : "#64748b")};
  font-size: 12px;
  font-weight: 700;
`;

const SendBtn = styled.button`
  min-height: 44px;
  padding: 0 16px;
  border-radius: 14px;
  border: none;
  background: linear-gradient(135deg, #f59e0b, #facc15);
  color: #04111f;
  font-weight: 900;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Toast = styled(motion.div)`
  position: absolute;
  right: 18px;
  bottom: calc(110px + env(safe-area-inset-bottom, 0px));
  padding: 10px 14px;
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.16);
  color: #e2e8f0;
  font-size: 12px;
  font-weight: 800;
  box-shadow: 0 18px 44px rgba(2, 6, 23, 0.34);

  @media (max-width: 640px) {
    right: 14px;
    left: 14px;
    bottom: calc(104px + env(safe-area-inset-bottom, 0px));
  }
`;

const EmptyRoadmapState = styled.div`
  flex: 1;
  min-height: 0;
  display: grid;
  place-items: center;
  padding: 24px;
`;

const EmptyRoadmapCard = styled.div`
  width: min(100%, 560px);
  padding: 28px 24px;
  border-radius: 28px;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(148, 163, 184, 0.14);
  text-align: center;
`;

const EmptyRoadmapEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const EmptyRoadmapTitle = styled.div`
  margin-top: 18px;
  color: #f8fafc;
  font-size: 28px;
  line-height: 1.12;
  font-weight: 1000;
`;

const EmptyRoadmapText = styled.div`
  margin-top: 12px;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.75;
`;

const EmptyRoadmapCTA = styled.button`
  margin-top: 22px;
  min-height: 46px;
  padding: 0 18px;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #f59e0b, #facc15);
  color: #04111f;
  font-weight: 900;
  cursor: pointer;
`;

const getTodayKey = () => new Date().toISOString().slice(0, 10);

const getMessageTimestamp = (message, fallbackIndex) => {
  if (message?.meta?.timestamp) {
    return new Date(message.meta.timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return fallbackIndex === 0 ? "Today" : "";
};

export default function AIMentorChatPage({
  onClose,
  assistantMode: assistantModeProp,
  dayContext: dayContextProp,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const messagesRef = useRef(null);
  const replySound = useRef(null);
  const inputRef = useRef(null);

  const [usage, setUsage] = useState(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [roadmapReady, setRoadmapReady] = useState(true);
  const [roadmapCheckLoading, setRoadmapCheckLoading] = useState(true);
  const [hasProAccess, setHasProAccess] = useState(false);
  const [planCheckLoading, setPlanCheckLoading] = useState(true);
  const [copyState, setCopyState] = useState("");
  const [messages, setMessages] = useState([STARTER_MESSAGE]);

  const assistantMode =
    assistantModeProp || location.state?.assistantMode || "roadmap";
  const productDayContext = dayContextProp || location.state?.dayContext || null;
  const isProductAssistant = assistantMode === "product-development";
  const shouldStartFresh = typeof onClose === "function";

  const contextualStarterMessage = useMemo(() => {
    if (!isProductAssistant || !productDayContext) return STARTER_MESSAGE;

    return {
      role: "assistant",
      content: `I can help you only with today’s product development task: ${productDayContext.title}. Ask me for step-by-step guidance, implementation order, code direction, exact files to create, or which docs to open first so you can finish this day without getting lost.`,
      meta: {
        type: "greeting",
        timestamp: Date.now(),
      },
    };
  }, [isProductAssistant, productDayContext]);

  const starterPrompts = useMemo(() => {
    if (!isProductAssistant || !productDayContext) return STARTER_PROMPTS;

    return [
      `Break "${productDayContext.title}" into small steps`,
      `How do I build "${productDayContext.title}" today?`,
      `Give me code direction for "${productDayContext.topic}"`,
      "Which references should I follow first?",
      `What should I do first for this task?`,
    ];
  }, [isProductAssistant, productDayContext]);

  useEffect(() => {
    replySound.current = new Audio("/mentor-reply.mp3");
    replySound.current.volume = 0.35;
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setRoadmapReady(false);
      setRoadmapCheckLoading(false);
      return;
    }

    async function checkRoadmap() {
      try {
        setRoadmapCheckLoading(true);
        const collectionName = isProductAssistant ? "product_roadmaps" : "roadmaps";
        const snap = await getDoc(doc(db, collectionName, user.uid));
        setRoadmapReady(snap.exists());
      } catch (error) {
        console.error("Failed to check roadmap status:", error);
        setRoadmapReady(false);
      } finally {
        setRoadmapCheckLoading(false);
      }
    }

    checkRoadmap();
  }, [user?.uid, isProductAssistant]);

  useEffect(() => {
    if (!user?.uid) {
      setHasProAccess(false);
      setPlanCheckLoading(false);
      return;
    }

    async function checkPlan() {
      try {
        setPlanCheckLoading(true);
        const userSnap = await getDoc(doc(db, "users", user.uid));
        setHasProAccess(userSnap.exists() && userSnap.data()?.plan === "PRO");
      } catch (error) {
        console.error("Failed to check plan status:", error);
        setHasProAccess(false);
      } finally {
        setPlanCheckLoading(false);
      }
    }

    checkPlan();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;
    if (!roadmapReady || !hasProAccess) return;

    if (shouldStartFresh) {
      setMessages([contextualStarterMessage]);
      return;
    }

    async function loadHistory() {
      try {
        const todayKey = getTodayKey();

        const q = query(
          collection(db, "aiChats"),
          where("uid", "==", user.uid),
          limit(100)
        );

        const snap = await getDocs(q);

        const historyMessages = snap.docs
          .map((d) => d.data())
          .filter((data) => data?.day === todayKey)
          .sort((a, b) => {
            const ta = a?.createdAt?.toMillis?.() || 0;
            const tb = b?.createdAt?.toMillis?.() || 0;
            return ta - tb;
          })
          .slice(-50)
          .flatMap((data) => {
            const timestamp = data?.createdAt?.toMillis?.() || Date.now();

            return [
              {
                role: "user",
                content: data.message,
                meta: {
                  timestamp,
                },
              },
              {
                role: "assistant",
                content: data.reply,
                actions: data.actions || null,
                meta: {
                  timestamp,
                },
              },
            ];
          });

        setMessages([contextualStarterMessage, ...historyMessages]);
      } catch (error) {
        console.error("Failed to load AI chat history:", error);
        setMessages([contextualStarterMessage]);
      }
    }

    loadHistory();
  }, [user?.uid, roadmapReady, hasProAccess, contextualStarterMessage, shouldStartFresh]);

  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;

    el.scrollTop = el.scrollHeight;
  }, [messages.length, loading]);

  useEffect(() => {
    if (!copyState) return;
    const t = setTimeout(() => setCopyState(""), 1600);
    return () => clearTimeout(t);
  }, [copyState]);

  const mentorTech = useMemo(() => {
    return user?.roadmap?.claritySnapshot?.technology || "Career";
  }, [user]);

  const assistantTitle =
    isProductAssistant
      ? "Your AI Daily Development Assistant"
      : mentorTech.toLowerCase() === "career"
      ? "Your AI Roadmap Assistant"
      : `Your ${mentorTech} AI Roadmap Assistant`;

  const usagePercent = usage
    ? Math.min(Math.round((usage.used / usage.limit) * 100), 100)
    : 0;

  const sendMessage = async (messageOverride) => {
    const nextMessage = (messageOverride ?? input).trim();

    if (!roadmapReady || !hasProAccess || !nextMessage || loading) return;

    const contextualMessage =
      isProductAssistant && productDayContext
        ? [
            "Product development day context:",
            `Task: ${productDayContext.title}`,
            `Topic: ${productDayContext.topic}`,
            `Objective: ${productDayContext.objective}`,
            productDayContext.deliverable
              ? `Deliverable: ${productDayContext.deliverable}`
              : null,
            Array.isArray(productDayContext.checklist) &&
            productDayContext.checklist.length
              ? `Checklist: ${productDayContext.checklist
                  .slice(0, 4)
                  .join(" | ")}`
              : null,
            Array.isArray(productDayContext.howTo) &&
            productDayContext.howTo.length
              ? `Suggested approach: ${productDayContext.howTo
                  .slice(0, 4)
                  .join(" | ")}`
              : null,
            Array.isArray(productDayContext.references) &&
            productDayContext.references.length
              ? `Reference docs: ${productDayContext.references
                  .slice(0, 2)
                  .map((ref) => `${ref.title} - ${ref.url}`)
                  .join(" | ")}`
              : null,
            `User question: ${nextMessage}`,
            "Answer only for this current day task. Give step-by-step implementation help, practical explanation, and suggest which reference to open first when useful.",
          ]
            .filter(Boolean)
            .join("\n")
        : nextMessage;

    const userMsg = {
      role: "user",
      content: nextMessage,
      meta: {
        timestamp: Date.now(),
      },
    };

    const prevMessages = messages.map(({ role, content }) => ({ role, content }));

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await askAIMentorChat({
        assistantMode,
        message: contextualMessage,
        history: prevMessages,
      });

      const reply = res.data?.reply;
      const usageData = res.data?.usage;
      const timestamp = Date.now();

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: reply,
          actions: res.data?.actions || null,
          meta: {
            timestamp,
            type: res.data?.meta?.type || "reply",
          },
        },
      ]);

      if (replySound.current) {
        replySound.current.currentTime = 0;
        replySound.current.play().catch(() => {});
      }

      if (usageData) {
        setUsage(usageData);
      }
    } catch (err) {
      const errorMessage =
        err?.details ||
        err?.message ||
        "Something went wrong. Try again in a moment.";

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: errorMessage,
          meta: {
            timestamp: Date.now(),
            type: "error",
          },
        },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const saveNote = async (content, relatedDay) => {
    try {
      await saveNoteFn({ content, relatedDay });
      setCopyState("Saved to notes");
    } catch (e) {
      setCopyState("Could not save note");
    }
  };

  const handleCopy = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopyState("Copied reply");
    } catch (e) {
      setCopyState("Copy failed");
    }
  };

  const resetLocalChat = () => {
    setMessages([contextualStarterMessage]);
    setInput("");
    inputRef.current?.focus();
  };

  const handleBack = () => {
    if (typeof onClose === "function") {
      onClose();
      return;
    }

    navigate(-1);
  };

  return (
    <Page>
      <Header>
        <BackBtn onClick={handleBack}>
          <ArrowLeft size={18} />
        </BackBtn>

        <HeaderBody>
          <HeaderTop>
            <div>
              <Title>{assistantTitle}</Title>
              <Sub>
                {isProductAssistant && productDayContext
                  ? `Focused only on today's build task: ${productDayContext.title}. Ask for step-by-step implementation help, code direction, or what to do first.`
                  : "Ask about today&apos;s roadmap, a blocker, topic clarity, or how to make steady progress without overthinking."}
              </Sub>
            </div>

            <Presence>
              <OnlineDot />
              Available now
            </Presence>
          </HeaderTop>
        </HeaderBody>
      </Header>

      <UtilityBar>
        <UtilityBtn onClick={resetLocalChat}>
          <RotateCcw size={14} />
          New chat
        </UtilityBtn>
      </UtilityBar>

      {usage && (
        <UsageWrap>
          <UsageRow>
            <span>Daily mentor usage</span>
            <span>
              {usage.used} / {usage.limit}
            </span>
          </UsageRow>

          <UsageBar>
            <UsageFill percent={usagePercent} />
          </UsageBar>

          <div style={{ fontSize: 11, color: "#64748b", marginTop: 7 }}>
            Resets at midnight. Keep questions focused so the mentor can help better.
          </div>
        </UsageWrap>
      )}

      <ChatWrapper>
        {roadmapCheckLoading || planCheckLoading ? (
          <ScrollArea />
        ) : !roadmapReady ? (
          <EmptyRoadmapState>
            <EmptyRoadmapCard>
              <EmptyRoadmapEyebrow>
                <Sparkles size={13} />
                Roadmap required
              </EmptyRoadmapEyebrow>
              <EmptyRoadmapTitle>
                {isProductAssistant
                  ? "Create your product roadmap first"
                  : "Create your roadmap first"}
              </EmptyRoadmapTitle>
              <EmptyRoadmapText>
                {isProductAssistant
                  ? "Your AI Daily Development Assistant needs product roadmap context before it can help with today’s build task."
                  : "Your Career Assistant needs roadmap context before it can help with today&apos;s work, blockers, and next-step guidance."}
              </EmptyRoadmapText>
              <EmptyRoadmapCTA
                onClick={() =>
                  navigate(
                    isProductAssistant
                      ? "/create-product-roadmap"
                      : "/career-clarity-home"
                  )
                }
              >
                {isProductAssistant ? "Create Product Roadmap" : "Create My Roadmap"}
              </EmptyRoadmapCTA>
            </EmptyRoadmapCard>
          </EmptyRoadmapState>
        ) : !hasProAccess ? (
          <EmptyRoadmapState>
            <EmptyRoadmapCard>
              <EmptyRoadmapEyebrow>
                <Sparkles size={13} />
                AI assistance locked
              </EmptyRoadmapEyebrow>
              <EmptyRoadmapTitle>
                {isProductAssistant
                  ? "Daily development help is part of PRO"
                  : "Daily AI roadmap help is part of PRO"}
              </EmptyRoadmapTitle>
              <EmptyRoadmapText>
                {isProductAssistant
                  ? "Your product roadmap is ready. Activate PRO to get task-specific daily development help, implementation breakdowns, and step-by-step guidance for today’s build."
                  : "Your roadmap is ready. Activate PRO to get daily AI roadmap assistance for today&apos;s task, blockers, prioritization, and next steps inside this mentor chat."}
              </EmptyRoadmapText>
              <div
                style={{
                  marginTop: 18,
                  padding: "14px 16px",
                  borderRadius: 18,
                  border: "1px solid rgba(148, 163, 184, 0.12)",
                  background: "rgba(255,255,255,0.03)",
                  color: "#cbd5e1",
                  lineHeight: 1.7,
                  fontSize: 14,
                }}
              >
                Includes daily roadmap guidance, faster unblock help, and
                focused next-step support based on your active plan.
              </div>
              <EmptyRoadmapCTA onClick={() => navigate("/pricing")}>
                Activate PRO
              </EmptyRoadmapCTA>
            </EmptyRoadmapCard>
          </EmptyRoadmapState>
        ) : (
          <ScrollArea>
            <PromptsScroller>
              {starterPrompts.map((prompt) => (
                <PromptChip key={prompt} onClick={() => sendMessage(prompt)}>
                  <Lightbulb size={14} />
                  {prompt}
                </PromptChip>
              ))}
            </PromptsScroller>

            <Messages ref={messagesRef}>
              {messages.map((m, i) => (
                <ChatRow key={`${m.role}-${i}`} role={m.role}>
                  <Avatar role={m.role}>
                    {m.role === "user" ? <User size={18} /> : <Bot size={18} />}
                  </Avatar>

                  <BubbleWrap>
                    <BubbleMeta role={m.role}>{getMessageTimestamp(m, i)}</BubbleMeta>

                    {m.role === "user" ? (
                      <UserBubble
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.18 }}
                      >
                        {m.content}
                      </UserBubble>
                    ) : (
                      <MentorBubble
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.18 }}
                      >
                        {formatMentorText(m.content)}

                        {m.role === "assistant" && (
                          <BubbleActions>
                            <ActionBtn tone="primary" onClick={() => handleCopy(m.content)}>
                              <Copy size={13} />
                              Copy
                            </ActionBtn>

                            {m.actions?.showRoadmap && (
                              <ActionBtn
                                tone="primary"
                                onClick={() => navigate("/my-roadmap")}
                              >
                                <Sparkles size={13} />
                                View roadmap
                              </ActionBtn>
                            )}

                            {m.meta?.type !== "greeting" && m.actions?.allowSaveNote && (
                              <ActionBtn
                                tone="success"
                                onClick={() =>
                                  saveNote(m.content, m.actions?.relatedDay)
                                }
                              >
                                <Check size={13} />
                                Save note
                              </ActionBtn>
                            )}
                          </BubbleActions>
                        )}
                      </MentorBubble>
                    )}
                  </BubbleWrap>
                </ChatRow>
              ))}

              {loading && (
                <Typing>
                  <TypingDots>
                    <span />
                    <span />
                    <span />
                  </TypingDots>
                  Mentor is thinking...
                </Typing>
              )}
            </Messages>
          </ScrollArea>
        )}

        <Composer>
          <ComposerShell>
            <Input
              ref={inputRef}
              rows={1}
              placeholder={
                isProductAssistant
                  ? "Ask how to build today’s task, what code to write first, or how to implement the current feature..."
                  : "Ask your mentor about today's roadmap, a blocker, or what to focus on next..."
              }
              value={input}
              maxLength={1200}
              disabled={!roadmapReady || !hasProAccess}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
            />

            <ComposerFooter>
              <ComposerHint>
                Press <strong>Enter</strong> to send, <strong>Shift + Enter</strong> for a new line.
              </ComposerHint>

              <ComposerActions>
                <CharacterCount warn={input.length > 1000}>
                  {input.length}/1200
                </CharacterCount>

                <SendBtn
                  onClick={() => sendMessage()}
                  disabled={!roadmapReady || !hasProAccess || loading || !input.trim()}
                >
                  <Send size={16} />
                  Send
                </SendBtn>
              </ComposerActions>
            </ComposerFooter>
          </ComposerShell>
        </Composer>
      </ChatWrapper>

      <AnimatePresence>
        {copyState && (
          <Toast
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            {copyState}
          </Toast>
        )}
      </AnimatePresence>
    </Page>
  );
}
